import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Clock, Eye, Flame, Play } from "lucide-react";
import { useSpecialty } from "../SpecialtyContext";
import { API_URL } from "../config";
import ReadWatchToggle from "./ReadWatchToggle";
import SeriesStrip from "./SeriesStrip";
import ContinueWatching from "./ContinueWatching";
import { cardSnippet } from "../lectureText";
import { AuthorLink } from "../authorLookup";

const TYPE_COLORS = {
  "Вебинар": "#006870",
  "Лекция": "#007c84",
  "Мастер-класс": "#15803d",
  "Материал": "#e5005b",
};
const MATERIAL_TYPES = ["Лекция", "Мастер-класс", "Вебинар", "Материал"];

const PAGE_SIZE = 10;

export function LectureCard({ l }) {
  const typeColor = TYPE_COLORS[l.material_type] || "#888";
  const snippet = cardSnippet(l.description);
  return (
    <article className="card lecture-card">
      <Link to={`/lectures/${l.id}`} className="lecture-thumb" aria-label={l.title} tabIndex={-1}>
        {l.thumbnail_url
          ? <img src={l.thumbnail_url} alt="" loading="lazy" />
          : <span className="lecture-thumb-fallback" />}
        <span className="lecture-play"><Play size={22} fill="currentColor" strokeWidth={0} /></span>
        {l.duration && <span className="lecture-duration"><Clock size={12} strokeWidth={2.4} /> {l.duration}</span>}
      </Link>
      <div className="lecture-body">
        <div className="lecture-meta">
          {l.material_type && <span className="lecture-type" style={{ background: typeColor }}>{l.material_type}</span>}
          {typeof l.views === "number" && l.views > 0 && (
            <span className="lecture-views"><Eye size={13} strokeWidth={2} /> {l.views}</span>
          )}
        </div>
        <Link to={`/lectures/${l.id}`} className="lecture-title">{l.title}</Link>
        {snippet && <p className="lecture-snippet">{snippet}</p>}
        <div className="lecture-foot">
          <AuthorLink name={l.author} className="lecture-author" />
          <Link to={`/lectures/${l.id}`} className="lecture-watch">Смотреть →</Link>
        </div>
      </div>
    </article>
  );
}

export default function Lectures() {
  const [lectures, setLectures] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [sortTop, setSortTop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const { specialty } = useSpecialty();
  const searchDebounce = useRef(null);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const fetchPage = useCallback((offset) => {
    const params = new URLSearchParams();
    if (specialty?.id) params.set("specialty", specialty.id);
    if (search) params.set("search", search);
    if (typeFilter) params.set("material_type", typeFilter);
    if (sortTop) params.set("sort", "views");
    params.set("limit", PAGE_SIZE);
    params.set("offset", offset);
    return fetch(`${API_URL}/lectures?${params}`).then(r => r.ok ? r.json() : []);
  }, [specialty, search, typeFilter, sortTop]);

  useEffect(() => {
    setLoading(true);
    fetchPage(0)
      .then(data => {
        setLectures(data);
        setHasMore(data.length === PAGE_SIZE);
      })
      .catch(() => { setLectures([]); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [fetchPage]);

  const loadMore = () => {
    setLoading(true);
    fetchPage(lectures.length)
      .then(data => {
        setLectures(prev => [...prev, ...data]);
        setHasMore(data.length === PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  };

  return (
    <section>
      <h1 className="section-title">Материалы</h1>
      <ReadWatchToggle />
      {!search && !typeFilter && !sortTop && <ContinueWatching />}
      {!search && !typeFilter && !sortTop && <SeriesStrip specialtyId={specialty?.id} />}

      <div style={{ marginBottom: 12 }}>
        <input
          type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
          placeholder="Поиск по материалам…"
          style={{ width: "100%", maxWidth: 360, padding: "8px 14px", borderRadius: 8,
            border: "1px solid #c4ceda", fontSize: "0.92rem", outline: "none", boxSizing: "border-box" }}
        />
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
        <button
          onClick={() => setTypeFilter(null)}
          style={{
            background: !typeFilter ? "#007c84" : "#f0f2f8",
            color: !typeFilter ? "#fff" : "#444",
            border: "none", borderRadius: 14, padding: "4px 12px",
            fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
          }}>
          Все
        </button>
        {MATERIAL_TYPES.map(t => (
          <button key={t} onClick={() => setTypeFilter(prev => prev === t ? null : t)}
            style={{
              background: typeFilter === t ? TYPE_COLORS[t] : "#f0f2f8",
              color: typeFilter === t ? "#fff" : "#444",
              border: "none", borderRadius: 14, padding: "4px 12px",
              fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
            }}>
            {t}
          </button>
        ))}
        <button
          onClick={() => setSortTop(prev => !prev)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: sortTop ? "#d97706" : "#f0f2f8",
            color: sortTop ? "#fff" : "#444",
            border: "none", borderRadius: 14, padding: "4px 12px",
            fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", marginLeft: "auto",
          }}>
          <Flame size={13} strokeWidth={2.2} /> Топ по просмотрам
        </button>
      </div>

      {!loading && lectures.length === 0 && (
        <p style={{ color: "#999", fontStyle: "italic" }}>
          {search ? "Ничего не найдено." : "Материалов пока нет. Добавьте первый через CMS."}
        </p>
      )}
      {lectures.map(l => <LectureCard key={l.id} l={l} />)}

      {loading && <p style={{ color: "#888", textAlign: "center" }}>Загрузка…</p>}

      {!loading && hasMore && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button onClick={loadMore}
            style={{ background: "#f0f2f8", color: "#007c84", border: "1.5px solid #c4ceda",
              borderRadius: 8, padding: "8px 24px", cursor: "pointer", fontSize: "0.92rem" }}>
            Показать ещё
          </button>
        </div>
      )}
    </section>
  );
}
